import type { ReactNode } from "react";
import * as RadixDialog from "@radix-ui/react-dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { Icon } from ".";

export type DialogProps = {
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (arg: boolean) => void;
  children: ReactNode;
  onInteractOutside?: (event: Event) => void;
  className?: string;
  withClose?: boolean;
  isFullScreen?: boolean;
  overLayZIndex?: number | string;
};

const baseClasses = "fixed z-40 bg-white shadow-lg will-change-[transform,opacity]";

const fullscreenClasses =
  "data-[state=closed]:animate-fadeOut data-[state=open]:animate-fadeIn top-0 left-0 h-screen w-screen overflow-y-auto px-8 py-10";

const modalClasses =
  "data-[state=closed]:animate-dialogContentHide data-[state=open]:animate-dialogContentShow top-[50%] left-[50%] flex max-h-[85vh] translate-x-[-50%] translate-y-[-50%] flex-col overflow-hidden rounded-3xl px-8 py-10";

const modalCloseButtonClasses =
  "group absolute -top-1 right-0 z-10 grid h-10 w-10 place-items-center rounded-full transition-all duration-75 hover:bg-gray-800/5 focus:outline-none focus:bg-gray-800/5";

const fullscreenCloseButtonClasses =
  "group absolute top-8 right-8 grid h-10 w-10 place-items-center rounded-full bg-white transition-all duration-75 hover:bg-gray-800/5";

export const Dialog = ({
  children,
  trigger,
  open,
  onInteractOutside,
  onOpenChange,
  className,
  withClose = true,
  isFullScreen = false,
  overLayZIndex = 40,
}: DialogProps) => (
  <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
    <RadixDialog.Trigger asChild>{trigger}</RadixDialog.Trigger>
    <RadixDialog.Portal>
      <RadixDialog.Overlay
        className={twMerge(
          "data-[state=closed]:animate-fadeOut data-[state=open]:animate-fadeIn fixed inset-0 bg-gray-900/50",
          `z-${overLayZIndex}`,
        )}
      />
      <VisuallyHidden.Root asChild>
        <RadixDialog.Title />
      </VisuallyHidden.Root>
      <RadixDialog.Content
        aria-describedby={undefined}
        onInteractOutside={onInteractOutside}
        className={twMerge(
          clsx(
            baseClasses,
            isFullScreen
              ? fullscreenClasses
              : clsx(
                  modalClasses,
                  (() => {
                    const hasCustomWidth = className?.match(/w-\[/);
                    return hasCustomWidth ? "" : "w-max max-w-[90%] lg:max-w-[50%]";
                  })(),
                ),
          ),
          className,
        )}>
        {isFullScreen ? (
          <>
            {children}
            {withClose ? (
              <RadixDialog.Close asChild>
                <button className={fullscreenCloseButtonClasses} aria-label="Close">
                  <Icon type="x" sizeClasses="h-3 w-3" className="transition group-hover:text-white" />
                </button>
              </RadixDialog.Close>
            ) : null}
          </>
        ) : (
          <div className="modal-scroll-outer relative">
            <div
              className={twMerge(
                "modal-scroll-inner modal-scrollbar p-px",
                (() => {
                  if (!className) return className;
                  if (className.includes("w-[") || className.includes("h-[")) {
                    return className
                      .replaceAll(/w-\[[^\]]+\]!?/g, "")
                      .replaceAll(/h-\[[^\]]+\]!?/g, "")
                      .replaceAll(/\s+/g, " ")
                      .trim();
                  }
                  return className;
                })(),
              )}>
              {children}
            </div>
            {withClose ? (
              <RadixDialog.Close asChild>
                <button
                  className={modalCloseButtonClasses}
                  aria-label="Close">
                  <Icon type="x" sizeClasses="h-3 w-3" className="group-hover:text-primary-100 transition" />
                </button>
              </RadixDialog.Close>
            ) : null}
          </div>
        )}
      </RadixDialog.Content>
    </RadixDialog.Portal>
  </RadixDialog.Root>
);
